import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { KaboodleModal } from "components/Modals";
import UploadFile from "components/UploadFile/UploadFile";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { setIsFileLoading } from "reducers/main";
import { useGetAdjustersQuery } from "services/adjuster-api";
import { useLazyGetAdjusterImportTemplateQuery } from "services/api";
import { useUploadAdjusterImportForClientMutation } from "services/client-api";

type UploadAdjusterTemplateModalPropTypes = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  importType: OptionType;
  includeClaims: boolean;
};

const UploadAdjusterTemplateModal = ({
  isOpen,
  onClose,
}: UploadAdjusterTemplateModalPropTypes) => {
  const { reset, handleSubmit } = useForm<FormData>({
    defaultValues: {
      importType: { label: "Claim", value: "C" },
      includeClaims: false,
    },
  });

  const [triggerGetImportTemplate, { isError, isLoading }] =
    useLazyGetAdjusterImportTemplateQuery();

  const footerContent = useMemo(() => {
    return <div>{/* commented button code */}</div>;
  }, [
    handleSubmit,
    isError,
    isLoading,
    onClose,
    reset,
    triggerGetImportTemplate,
  ]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const dispatch = useDispatch();
  const [uploadClient] = useUploadAdjusterImportForClientMutation();
  const { clientId } = useParams<{ clientId?: string }>();
  const { refetch: refetchAdjusters } = useGetAdjustersQuery(
    { client_id: clientId },
    { skip: !clientId },
  );

  const handleAdjusterFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await uploadClient({
        id: clientId,
        file: formData,
      }).unwrap();
      dispatch(setIsFileLoading(false));
      refetchAdjusters();
      onClose();
      KaboodleToaster.success("Adjusters uploaded successfully");
    } catch (error) {
      const errorData = error?.data?.errors || "";
      if (errorData) {
        KaboodleToaster.error(errorData);
        console.error(error);
      } else {
        KaboodleToaster.error("Error uploading adjusters:");
        console.error(error);
      }
    }
  };

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card
        style={{ marginBottom: "10px" }}
        heading="Adjuster Upload"
        type="full-width inverse"
      >
        <UploadFile onUpload={handleAdjusterFileUpload} />
      </Card>
    </KaboodleModal>
  );
};

export default UploadAdjusterTemplateModal;
